import HTTP from "@/_helpers/http";
import { TRADING__API } from "@/_helpers/urls";
import router from "@/router";
import toast from "@/services/Notification";
import { TRADING__ACTIONS } from "@/store/action-types";
import { TRADING__MUTATIONS } from "@/store/mutation-types";

const state = {
  tradingAccountLiveTrades: [],
  accountSpecificLiveTradesSummary: null,
  totalLiveTrades: 0,
  leverages: [],
  leverage_account_type: null,
  accountTypes: [],
  demoAccountTypes: [],
  liveAccounts: [],
  demoAccounts: [],
  mt5AccountDetails: null,
  tradeHistory: [],
  allLiveTrades: [],
  allCloseTrades: [],
  allLiveAccounts: [],
  accountSummary: null,
  totalLiveAccounts: 0,
  liveTradingAccounts: [],
  investorAccountLiveTrades: [],
  investorAccountSummary: null,
  investorTradingAccountTradehistory: [],
  managerAccountBalanceCardDetails: null,
  managerDetails: null,
};

const getters = {};

const mutations = {
  [TRADING__MUTATIONS.MANAGERACCOUNTBALANCECARDDETAILS](state, data) {
    state.managerAccountBalanceCardDetails =
      data.managerAccounBalanceCardtDetails;
  },

  [TRADING__MUTATIONS.VIEWMANAGERDETAILS](state, data) {
    state.managerDetails = data.managerAcc;
  },

  [TRADING__MUTATIONS.CLIENTLIVETRADES](state, data) {
    state.tradingAccountLiveTrades = data.live_trades;
    state.accountSpecificLiveTradesSummary = data.accountSummary;
  },

  [TRADING__MUTATIONS.TOTALLIVETRADES](state, data) {
    state.totalLiveTrades = data;
  },

  [TRADING__MUTATIONS.ACCOUNTTYPES](state, data) {
    state.accountTypes = data.accountTypes;
  },

  [TRADING__MUTATIONS.LEVERAGES](state, data) {
    state.leverages = data.leverages;
  },

  [TRADING__MUTATIONS.LIVEACCOUNTS](state, data) {
    state.liveAccounts = data.liveAccounts;
  },

  [TRADING__MUTATIONS.DEMOACCOUNTS](state, data) {
    state.demoAccounts = data.demoAccounts;
  },

  [TRADING__MUTATIONS.MT5ACCOUNTDETAILS](state, data) {
    state.mt5AccountDetails = data;
  },

  [TRADING__MUTATIONS.TRADEHISTORY](state, data) {
    state.tradeHistory = data.closed_trades;
  },

  [TRADING__MUTATIONS.ALLTRADEDETAILS](state, data) {
    state.allLiveTrades = data.live_trades;
    state.allCloseTrades = data.closed_trades;
    state.allLiveAccounts = data.liveAccounts;
    state.accountSummary = data.accountSummary;
  },

  [TRADING__MUTATIONS.TOTALLIVEACCOUNTS](state, data) {
    state.totalLiveAccounts = data.liveAccounts;
  },

  [TRADING__MUTATIONS.DEMOACCOUNTTYPES](state, data) {
    state.demoAccountTypes = data.accountTypes;
  },

  [TRADING__MUTATIONS.LIVETRADINGACCOUNTS](state, data) {
    state.liveTradingAccounts = data.liveAccounts;
  },

  [TRADING__MUTATIONS.INVESTORACCOUNTLIVETRADES](state, data) {
    state.investorAccountLiveTrades = data.live_trades;
    state.investorAccountSummary = data.accountSummary;
  },

  [TRADING__MUTATIONS.INVESTORACCOUNTTRADEHISTORY](state, data) {
    state.investorTradingAccountTradehistory = data.closed_trades;
  },
  [TRADING__MUTATIONS.LEVERAGEACCOUNTTYPE](state, data) {
    state.leverage_account_type = data.leverages;
  },
};

const actions = {
  async [TRADING__ACTIONS.MANAGERACCOUNTBALANCECARDDETAILS]({ commit }, id) {
    try {
      const response = await HTTP().get(
        TRADING__API.MANAGERACCOUNTBALANCECARDDETAILS + `/${id}`
      );
      commit(
        TRADING__MUTATIONS.MANAGERACCOUNTBALANCECARDDETAILS,
        response.data
      );
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.LEVERAGEACCOUNTTYPE]({ commit }, id) {
    try {
      const response = await HTTP().get(
        TRADING__API.LEVERAGEACCOUNTTYPE + `/${id}`
      );
      commit(TRADING__MUTATIONS.LEVERAGEACCOUNTTYPE, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.CLIENTLIVETRADES]({ commit }, id) {
    try {
      const response = await HTTP().get(
        TRADING__API.CLIENTLIVETRADES + `/${id}`
      );
      commit(TRADING__MUTATIONS.CLIENTLIVETRADES, response.data);
      commit(TRADING__MUTATIONS.TOTALLIVETRADES, response.data.total);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.ACCOUNTTYPES]({ commit }) {
    try {
      const response = await HTTP().get(TRADING__API.ACCOUNTTYPES);
      commit(TRADING__MUTATIONS.ACCOUNTTYPES, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.LIVETRADINGACCOUNTS]({ commit }) {
    try {
      const response = await HTTP().get(TRADING__API.LIVETRADINGACCOUNTS);
      commit(TRADING__MUTATIONS.LIVETRADINGACCOUNTS, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.DEMOACCOUNTTYPES]({ commit }) {
    try {
      const response = await HTTP().get(TRADING__API.DEMOACCOUNTTYPES);
      commit(TRADING__MUTATIONS.DEMOACCOUNTTYPES, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.LEVERAGES]({ commit }) {
    try {
      const response = await HTTP().get(TRADING__API.LEVERAGES);
      commit(TRADING__MUTATIONS.LEVERAGES, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.OPENTRADINGACCOUNT]({ commit }, data) {
    try {
      const response = await HTTP().post(TRADING__API.OPENTRADINGACCOUNT, {
        newTradingAccount: data,
      });

      if (response.status === 200) {
        toast.success(response.data.message || "New account created");
        return { success: true };
      }
    } catch (error) {
      console.error(error);
      if (error.response.data.agreement === false) {
        toast.warning(error.response.data.message);
        router.push("/terms-and-condition");
        return { success: false };
      }

      toast.error(error.response.data.message || "Account creation failed");
      return { success: false };
    }
  },

  async [TRADING__ACTIONS.OPENNEWDEMOACCOUNT]({ commit }, data) {
    try {
      const response = await HTTP().post(TRADING__API.OPENNEWDEMOACCOUNT, {
        newTradingAccount: data,
      });
      if (response.status === 200) {
        toast.success("New demo account created");
        return;
      }
    } catch (error) {
      console.error(error);
      toast.error("Account creation failed");
    }
  },

  async [TRADING__ACTIONS.LIVEACCOUNTS]({ commit }) {
    try {
      const resposne = await HTTP().get(TRADING__API.LIVEACCOUNTS);
      commit(TRADING__MUTATIONS.LIVEACCOUNTS, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.DEMOACCOUNTS]({ commit }) {
    try {
      const resposne = await HTTP().get(TRADING__API.DEMOACCOUNTS);
      commit(TRADING__MUTATIONS.DEMOACCOUNTS, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.MT5ACCOUNTDETAILS]({ state, commit }, queryParams) {
    try {
      const response = await HTTP().get(
        `${TRADING__API.MT5ACCOUNTDETAILS}/${queryParams}`
      );

      commit(TRADING__MUTATIONS.MT5ACCOUNTDETAILS, response.data.liveAccounts);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.TRADEHISTORY]({ commit }, id) {
    try {
      const response = await HTTP().get(TRADING__API.TRADEHISTORY + `/${id}`);
      commit(TRADING__MUTATIONS.TRADEHISTORY, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.ALLTRADEDETAILS]({ commit }) {
    try {
      const response = await HTTP().get(TRADING__API.ALLTRADEDETAILS);
      commit(TRADING__MUTATIONS.ALLTRADEDETAILS, response.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.TOTALLIVEACCOUNTS]({ commit }) {
    try {
      const resposne = await HTTP().get(TRADING__API.TOTALLIVEACCOUNTS);
      commit(TRADING__MUTATIONS.TOTALLIVEACCOUNTS, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.INVESTORACCOUNTLIVETRADES]({ commit }, id) {
    try {
      const resposne = await HTTP().get(
        TRADING__API.INVESTORACCOUNTLIVETRADES + `/${id}`
      );
      commit(TRADING__MUTATIONS.INVESTORACCOUNTLIVETRADES, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.INVESTORACCOUNTTRADEHISTORY]({ commit }, id) {
    try {
      const resposne = await HTTP().get(
        TRADING__API.INVESTORACCOUNTTRADEHISTORY + `/${id}`
      );
      commit(TRADING__MUTATIONS.INVESTORACCOUNTTRADEHISTORY, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },

  async [TRADING__ACTIONS.VIEWMANAGERDETAILS]({ commit }, id) {
    try {
      const resposne = await HTTP().get(
        TRADING__API.VIEWMANAGERDETAILS + `/${id}`
      );
      commit(TRADING__MUTATIONS.VIEWMANAGERDETAILS, resposne.data);
    } catch (error) {
      console.error(error);
    }
  },
};

export default {
  namespaced: true,
  strict: process.env.NODE_ENV !== "production",
  state,
  getters,
  mutations,
  actions,
};
